import {hpcAttrs} from '@github-ui/hpc'
import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {testIdProps} from '@github-ui/test-id-props'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import {ProjectTemplateIcon, TableIcon} from '@primer/octicons-react'
import {Button, Spinner, VisuallyHidden} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useCallback, useEffect, useRef, useState} from 'react'

import {CopyProjectDialog, type CopyProjectDialogMode} from './CopyProjectDialog'
import {NewProjectForm} from './NewProjectForm'
import {OpenClosedProjects} from './OpenClosedProjects'
import type {ProjectListLoadingState} from './project-list-loading-state'
import type {ProjectSortValue} from './project-sort-options'
import {ProjectRow} from './ProjectRow'
import {ProjectSortMenu} from './ProjectSortMenu'
import styles from './RepositoryProjects.module.css'
import type {NewProjectAction, Project} from './types'
import {useConfirmUnlinkProject} from './use-confirm-unlink-project'
import {useUnlinkProject} from './use-unlink-project'

interface ProjectListProps {
  projects: Project[]
  openCount: number
  closedCount: number
  hasNextPage: boolean
  onLoadMore: () => void
  viewerCanPush: boolean
  /** Repo owner login, used as the form action prefix for the "New project" CTA. */
  owner: string
  /** Repo name, used as the form action prefix for the "New project" CTA. */
  repo: string
  /**
   * Hidden-field bundle for the "New project" CTA. `null` when the viewer can't
   * create a project here — the CTA is hidden in that case.
   */
  newProjectAction: NewProjectAction | null
  query: string
  /** The current open/closed filter state, used to tailor the empty-state copy. */
  state: 'open' | 'closed'
  sort: ProjectSortValue
  onSortChange: (value: ProjectSortValue) => void
  loadingState?: ProjectListLoadingState
  /** When true, the list is showing the templates view; affects blankslate copy and icon. */
  isTemplateView?: boolean
}

export function ProjectList({
  projects,
  openCount,
  closedCount,
  hasNextPage,
  onLoadMore,
  viewerCanPush,
  owner,
  repo,
  newProjectAction,
  query,
  state,
  sort,
  onSortChange,
  loadingState = 'ready',
  isTemplateView = false,
}: ProjectListProps) {
  const listItemsHeader = (
    <ListViewMetadata sectionFilters={<OpenClosedProjects openCount={openCount} closedCount={closedCount} />}>
      <ProjectSortMenu sort={sort} onSortChange={onSortChange} />
    </ListViewMetadata>
  )

  const {addToast} = useToastContext()
  const {mutate: mutateUnlinkProject} = useUnlinkProject({owner, repo})
  const {confirmUnlinkProject} = useConfirmUnlinkProject()
  // Hold `addToast` in a ref so the callback identity below stays stable across
  // toast events. The `ToastContextProvider` recreates its context value (and
  // therefore `addToast`) every time *any* toast fires anywhere in the app; without
  // this indirection, `handleUnlinkProject` would change on every such render and
  // defeat `ProjectRow`'s `memo()` for every row in the list.
  const addToastRef = useRef(addToast)
  useEffect(() => {
    addToastRef.current = addToast
  }, [addToast])
  // Owned here (not in `ProjectRowActionMenu`) so the per-call `onError` toast still
  // fires after the row optimistically unmounts. TanStack Query skips per-call
  // `mutate` callbacks when the calling component is unmounted, but `ProjectList`
  // stays mounted across cache-driven row removals. `mutate` is a stable reference
  // from `useMutation`, so this callback survives across renders.
  const handleUnlinkProject = useCallback(
    async (project: Project) => {
      const confirmed = await confirmUnlinkProject(project)
      if (!confirmed) return

      mutateUnlinkProject(
        {projectId: project.id},
        {
          onError: () => {
            addToastRef.current({
              type: 'error',
              message: `Couldn't unlink ${project.title}. Please try again.`,
            })
          },
        },
      )
    },
    [confirmUnlinkProject, mutateUnlinkProject],
  )

  const [activeCopyTarget, setActiveCopyTarget] = useState<{project: Project; mode: CopyProjectDialogMode} | null>(null)
  const {sendAnalyticsEvent} = useAnalytics()

  const handleCopyProject = useCallback(
    (project: Project) => {
      sendAnalyticsEvent('memex_copy_project', undefined, {project_id: project.id})
      setActiveCopyTarget({project, mode: 'copy'})
    },
    [sendAnalyticsEvent],
  )

  const handleCopyAsTemplate = useCallback(
    (project: Project) => {
      sendAnalyticsEvent('memex_event', undefined, {
        project_id: project.id,
        name: 'copy_as_template',
        ui: 'project_list',
      })
      setActiveCopyTarget({project, mode: 'template'})
    },
    [sendAnalyticsEvent],
  )

  const handleCopyDialogClose = useCallback(() => {
    setActiveCopyTarget(null)
  }, [])

  const projectRows =
    projects.length > 0
      ? projects.map(project => (
          <ProjectRow
            key={project.id}
            project={project}
            onCopyProject={handleCopyProject}
            onCopyAsTemplate={handleCopyAsTemplate}
            onUnlinkProject={handleUnlinkProject}
          />
        ))
      : null

  const renderEmptyState = () => {
    const hasActiveQuery = query.trim().length > 0
    if (hasActiveQuery) {
      return <NoMatchingProjectsBlankslate isTemplateView={isTemplateView} />
    }
    return isTemplateView ? (
      <EmptyRepoTemplatesBlankslate viewerCanPush={viewerCanPush} state={state} />
    ) : (
      <EmptyRepoProjectsBlankslate
        viewerCanPush={viewerCanPush}
        owner={owner}
        repo={repo}
        newProjectAction={newProjectAction}
        state={state}
      />
    )
  }

  // Announce results only once rows are stable on screen; stay silent while first-paint
  // loading or a query-change refetch is swapping the list out from under the user.
  const showResultsAnnouncement = loadingState === 'ready' || loadingState === 'loadingMore'
  const liveRegionMessage = showResultsAnnouncement
    ? buildResultsAnnouncement(projects.length, query, isTemplateView, state)
    : ''

  return (
    <>
      <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
        {liveRegionMessage}
      </VisuallyHidden>
      <div className={styles.projectListWrapper} {...hpcAttrs()} {...testIdProps('project-list-wrapper')}>
        <ListView
          {...testIdProps('repository-project-list-view')}
          title="Repository projects"
          metadata={listItemsHeader}
          titleHeaderTag="h2"
          singularUnits="project"
          pluralUnits="projects"
          as={projects.length > 0 ? 'ul' : 'div'}
          role={projects.length > 0 ? 'list' : 'status'}
        >
          {projectRows}
        </ListView>
        {loadingState === 'loading' ? (
          <div className={styles.loadingContainer} role="status" aria-label="Loading projects">
            <Spinner size="large" />
          </div>
        ) : (
          projects.length === 0 && renderEmptyState()
        )}
      </div>
      {hasNextPage && (
        <div className={styles.loadMoreButtonWrapper}>
          <Button
            variant="invisible"
            onClick={onLoadMore}
            className={styles.loadMoreButton}
            data-testid="load-more-projects-button"
            loading={loadingState === 'loadingMore'}
          >
            Load more projects
          </Button>
        </div>
      )}
      {activeCopyTarget && (
        <CopyProjectDialog
          project={activeCopyTarget.project}
          mode={activeCopyTarget.mode}
          onClose={handleCopyDialogClose}
        />
      )}
    </>
  )
}

function buildResultsAnnouncement(
  count: number,
  query: string,
  isTemplateView: boolean,
  state: 'open' | 'closed',
): string {
  const noun = isTemplateView ? 'template' : 'project'
  const plural = isTemplateView ? 'templates' : 'projects'
  const trimmedQuery = query.trim()
  if (count === 0) {
    if (trimmedQuery.length > 0) {
      return `No ${plural} matched \u201C${trimmedQuery}\u201D`
    }
    // Mirror the visible blankslate heading so AT users hear the same thing.
    if (state === 'closed') {
      return isTemplateView ? `No closed project ${plural}` : `No closed ${plural}`
    }
    return isTemplateView ? `No project ${plural}` : `No ${plural} found`
  }
  if (count === 1) {
    return `1 ${noun}`
  }
  return `${count} ${plural}`
}

function NoMatchingProjectsBlankslate({isTemplateView}: {isTemplateView: boolean}) {
  const Icon = isTemplateView ? ProjectTemplateIcon : TableIcon
  const label = isTemplateView ? 'project templates' : 'projects'
  return (
    <div className={styles.emptyState}>
      <Blankslate>
        <Blankslate.Visual>
          <Icon size="medium" />
        </Blankslate.Visual>
        <Blankslate.Heading>No {label} matched your search</Blankslate.Heading>
        <Blankslate.Description>
          Try a different search term or clear the search to see all {label}.
        </Blankslate.Description>
      </Blankslate>
    </div>
  )
}

interface EmptyRepoProjectsBlankslateProps {
  viewerCanPush: boolean
  owner: string
  repo: string
  newProjectAction: NewProjectAction | null
  state: 'open' | 'closed'
}

/**
 * The closed branch suppresses the "New project" CTA: filter-driven emptiness should
 * not look like a brand-new repo prompting the viewer to create a project.
 */
function EmptyRepoProjectsBlankslate({
  viewerCanPush,
  owner,
  repo,
  newProjectAction,
  state,
}: EmptyRepoProjectsBlankslateProps) {
  if (state === 'closed') {
    return (
      <div className={styles.emptyState}>
        <Blankslate>
          <Blankslate.Visual>
            <TableIcon size="medium" />
          </Blankslate.Visual>
          <Blankslate.Heading>No closed projects</Blankslate.Heading>
        </Blankslate>
      </div>
    )
  }

  return (
    <div className={styles.emptyState}>
      <Blankslate>
        <Blankslate.Visual>
          <TableIcon size="medium" />
        </Blankslate.Visual>
        <Blankslate.Heading>No projects found</Blankslate.Heading>
        <Blankslate.Description>
          {viewerCanPush
            ? 'There are no projects linked to this repository yet.'
            : 'There are no projects linked to this repository.'}
        </Blankslate.Description>
        {newProjectAction && (
          <NewProjectForm owner={owner} repo={repo} action={newProjectAction}>
            <Blankslate.PrimaryAction type="submit">New project</Blankslate.PrimaryAction>
          </NewProjectForm>
        )}
      </Blankslate>
    </div>
  )
}

interface EmptyRepoTemplatesBlankslateProps {
  viewerCanPush: boolean
  state: 'open' | 'closed'
}

function EmptyRepoTemplatesBlankslate({viewerCanPush, state}: EmptyRepoTemplatesBlankslateProps) {
  if (state === 'closed') {
    return (
      <div className={styles.emptyState}>
        <Blankslate>
          <Blankslate.Visual>
            <ProjectTemplateIcon size="medium" />
          </Blankslate.Visual>
          <Blankslate.Heading>No closed project templates</Blankslate.Heading>
        </Blankslate>
      </div>
    )
  }

  return (
    <div className={styles.emptyState}>
      <Blankslate>
        <Blankslate.Visual>
          <ProjectTemplateIcon size="medium" />
        </Blankslate.Visual>
        <Blankslate.Heading>
          {viewerCanPush ? 'Provide quick access to relevant project templates' : 'No project templates'}
        </Blankslate.Heading>
        {viewerCanPush && (
          <Blankslate.Description>
            Add project templates from your organization to view them here.
          </Blankslate.Description>
        )}
      </Blankslate>
    </div>
  )
}
